import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { patientsApi, appointmentsApi, consultationsApi, filesApi } from '@/services/api';
import { invoiceService } from '@/services/invoiceService';
import { formatDate, formatDateOnly, formatDateTime } from '@/utils/dateTime';
import { formatPhone, formatPatientAge, formatDoctorName } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Activity,
  FileText,
  Clock,
  Stethoscope,
  AlertTriangle,
  Heart,
  Upload,
  Download,
  Trash2,
  User,
  Plus,
  Printer,
  Receipt,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ExternalLink,
  Pill,
  Eye,
  FileCheck,
  Play,
  Filter,
  CreditCard,
  Search,
  ShieldCheck,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthorizedButton } from '@/components/common/AuthorizedButton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { PrescriptionPrintModal } from '@/components/prescriptions/PrescriptionPrintModal';
import { InvoiceModal } from '@/components/billing/InvoiceModal';
import { PaymentRecordModal } from '@/components/billing/PaymentRecordModal';
import { InvoicePrintModal } from '@/components/billing/InvoicePrintModal';

const PatientDetailPage = () => {
  const navigate = useNavigate();
  const { buildUrl, hospitalId, activeClinic, activeClinicId } = useClinic();
  const { id } = useParams();

  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [files, setFiles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // UI state
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [yearFilter, setYearFilter] = useState('ALL');
  const [searchFilter, setSearchFilter] = useState('');
  const [expandedCaseSheets, setExpandedCaseSheets] = useState({});

  // Modals state
  const [selectedConsultationForPrint, setSelectedConsultationForPrint] = useState(null);
  const [selectedInvoiceForModal, setSelectedInvoiceForModal] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [paymentRecordModalOpen, setPaymentRecordModalOpen] = useState(false);
  const [invoicePrintModalOpen, setInvoicePrintModalOpen] = useState(false);
  const [activeInvoiceForPayment, setActiveInvoiceForPayment] = useState(null);
  const [activeInvoiceForPrint, setActiveInvoiceForPrint] = useState(null);

  useEffect(() => {
    loadPatientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hospitalId]);

  const loadPatientData = async () => {
    try {
      setLoading(true);
      const [patientData, appointmentsData, consultationsData, filesData, invoicesData] =
        await Promise.all([
          patientsApi.getById(id, hospitalId),
          appointmentsApi.getAll({ patientId: id }, hospitalId).catch(() => ({ data: [] })),
          consultationsApi.getAll({ patientId: id }, hospitalId).catch(() => ({ data: [] })),
          filesApi.getByPatient(id, hospitalId).catch(() => []),
          invoiceService.getAll({ patientId: id }, hospitalId).catch(() => ({ data: [] })),
        ]);

      setPatient(patientData);
      setAppointments(
        appointmentsData?.data || (Array.isArray(appointmentsData) ? appointmentsData : [])
      );
      setConsultations(
        consultationsData?.data || (Array.isArray(consultationsData) ? consultationsData : [])
      );
      setFiles(Array.isArray(filesData) ? filesData : filesData?.data || []);
      setInvoices(
        invoicesData?.data || (Array.isArray(invoicesData) ? invoicesData : [])
      );
    } catch (error) {
      toast.error('Failed to load patient profile data');
      navigate(buildUrl('patients'));
    } finally {
      setLoading(false);
    }
  };

  const toggleCaseSheetExpand = (consId) => {
    setExpandedCaseSheets((prev) => ({
      ...prev,
      [consId]: !prev[consId],
    }));
  };

  // Compute total financial pending across patient's invoices
  const totalOutstanding = invoices.reduce(
    (sum, inv) => sum + (Number(inv.balanceAmount) || 0),
    0
  );

  const getInvoiceForConsultation = (consId) => {
    return invoices.find((inv) => inv.consultationId === consId);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="patient-detail-loading">
        <Skeleton className="h-16 w-full rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (!patient) return null;

  const ageDisplay = formatPatientAge(patient.age, patient.dateOfBirth);
  const initials =
    `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase() || 'P';

  // Filter consultations by year and search
  const filteredConsultations = consultations.filter((cons) => {
    const consDate = cons.date || cons.createdAt || '';
    if (yearFilter !== 'ALL') {
      if (!consDate.startsWith(yearFilter)) return false;
    }
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      const matchDiag = (cons.diagnosis || '').toLowerCase().includes(q);
      const matchDoc = formatDoctorName(cons.doctor).toLowerCase().includes(q);
      const matchComplaints = (cons.chiefComplaints || cons.symptoms || '').toLowerCase().includes(q);
      return matchDiag || matchDoc || matchComplaints;
    }
    return true;
  });

  const ongoingConsultation = filteredConsultations.find(
    (c) => c.status === 'DRAFT' || c.status === 'IN_PROGRESS'
  );
  const completedConsultations = filteredConsultations.filter(
    (c) => c.status !== 'DRAFT' && c.status !== 'IN_PROGRESS'
  );

  // Extract all medications across all visits for Medications Tab
  const allMedications = consultations.flatMap((cons) => {
    const rawRx = cons.prescriptions || cons.prescription || [];
    return rawRx.map((rx) => ({
      ...rx,
      visitDate: cons.date,
      doctor: cons.doctor,
      diagnosis: cons.diagnosis,
    }));
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16" data-testid="patient-detail-page">
      {/* 1. TOP PATIENT HEADER BANNER */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(buildUrl('patients'))}
              className="text-slate-500 hover:text-slate-900 rounded-full h-9 w-9"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <Avatar className="h-14 w-14 rounded-xl border-2 border-teal-100 shadow-inner">
              <AvatarFallback className="bg-teal-600 text-white font-bold text-lg rounded-xl">
                {initials}
              </AvatarFallback>
            </Avatar>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">
                  {patient.firstName} {patient.lastName}
                </h1>
                <AuthorizedButton
                  permission="PATIENT_WRITE"
                  tooltipMessage="You don't have access to edit patient details"
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(buildUrl(`patients/${id}/edit`))}
                  className="h-8 w-8 text-slate-400 hover:text-teal-600 rounded-md"
                  data-testid="edit-patient-btn"
                >
                  <Edit className="h-4 w-4" />
                </AuthorizedButton>
              </div>

              <div className="text-xs sm:text-sm text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                <span>
                  {patient.gender || 'Unknown gender'}, {ageDisplay !== '-' ? ageDisplay : 'Age N/A'}
                </span>
                <span className="text-slate-300">•</span>
                <span className="font-mono text-slate-700">{formatPhone(patient.phone)}</span>
                <span className="text-slate-300">•</span>
                <span className="font-mono font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded text-xs">
                  {patient.patientCode || `#${patient.id.slice(-6)}`}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions & Financial Status */}
          <div className="flex items-center gap-3 self-end sm:self-center">
            {/* Quick Call Button */}
            {patient.phone && (
              <a
                href={`tel:${patient.phone}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors shadow-xs"
              >
                <Phone className="h-3.5 w-3.5 text-teal-600" />
                <span className="font-mono">{formatPhone(patient.phone)}</span>
              </a>
            )}

            {/* Financial Pending Badge */}
            {totalOutstanding > 0 ? (
              <button
                type="button"
                onClick={() => setActiveTab('invoices')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer shadow-xs"
                title="Click to view invoices and clear pending balance"
              >
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                Pending • ₹{totalOutstanding.toLocaleString('en-IN')}
                <ExternalLink className="h-3 w-3 ml-0.5 text-amber-700" />
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                All Settled
              </span>
            )}

            {/* Expand / Collapse Header Details Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
              className="h-8 w-8 text-slate-600 border-slate-200 hover:bg-slate-100 rounded-lg"
              title={isHeaderExpanded ? 'Collapse demographic details' : 'Expand full demographic details'}
            >
              {isHeaderExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Expandable Demographics Accordion */}
        {isHeaderExpanded && (
          <div className="bg-slate-50/80 border-t border-slate-200 p-4 sm:p-5 grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] mb-1">
                Emergency Contact
              </p>
              {patient.emergencyContact?.name ? (
                <div>
                  <p className="font-medium text-slate-900">{patient.emergencyContact.name}</p>
                  <p className="text-slate-500">
                    {patient.emergencyContact.relationship || 'Relation'} •{' '}
                    <span className="font-mono">{formatPhone(patient.emergencyContact.phone)}</span>
                  </p>
                </div>
              ) : (
                <p className="text-slate-400">None specified</p>
              )}
            </div>

            <div>
              <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] mb-1">
                Blood Group & Email
              </p>
              <p className="font-medium text-slate-900">
                Blood: <span className="font-bold text-red-600">{patient.bloodGroup || 'Not Set'}</span>
              </p>
              <p className="text-slate-600 truncate">{patient.email || 'No email on file'}</p>
            </div>

            <div>
              <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] mb-1">
                Residential Address
              </p>
              {patient.address?.street || patient.address?.city ? (
                <p className="text-slate-700">
                  {patient.address.street ? `${patient.address.street}, ` : ''}
                  {patient.address.city || ''} {patient.address.state || ''}{' '}
                  {patient.address.pinCode ? `- ${patient.address.pinCode}` : ''}
                </p>
              ) : (
                <p className="text-slate-400">No address recorded</p>
              )}
            </div>

            <div>
              <p className="font-semibold text-slate-500 uppercase tracking-wider text-[10px] mb-1">
                Allergies & Alerts
              </p>
              {patient.allergies?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {patient.allergies.map((allergy, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-red-50 text-red-700 border-red-200 text-[10px] py-0"
                    >
                      {allergy}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400">No known drug allergies</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 2. HORIZONTAL NAVIGATION TABS */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs px-2">
          <TabsList className="w-full justify-start h-12 p-0 bg-transparent gap-2 overflow-x-auto">
            <TabsTrigger
              value="overview"
              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 data-[state=active]:font-semibold data-[state=active]:bg-transparent px-4 text-xs sm:text-sm"
              data-testid="tab-overview"
            >
              <User className="h-4 w-4 mr-2" />
              Patient Overview
            </TabsTrigger>
            <TabsTrigger
              value="examination"
              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 data-[state=active]:font-semibold data-[state=active]:bg-transparent px-4 text-xs sm:text-sm"
              data-testid="tab-examination"
            >
              <Eye className="h-4 w-4 mr-2" />
              Visual Examination
            </TabsTrigger>
            <TabsTrigger
              value="medications"
              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 data-[state=active]:font-semibold data-[state=active]:bg-transparent px-4 text-xs sm:text-sm"
              data-testid="tab-medications"
            >
              <Pill className="h-4 w-4 mr-2" />
              Medications ({allMedications.length})
            </TabsTrigger>
            <TabsTrigger
              value="background"
              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 data-[state=active]:font-semibold data-[state=active]:bg-transparent px-4 text-xs sm:text-sm"
              data-testid="tab-background"
            >
              <Heart className="h-4 w-4 mr-2" />
              Medical Background
            </TabsTrigger>
            <TabsTrigger
              value="paperwork"
              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 data-[state=active]:font-semibold data-[state=active]:bg-transparent px-4 text-xs sm:text-sm"
              data-testid="tab-paperwork"
            >
              <FileText className="h-4 w-4 mr-2" />
              Paperwork ({files.length})
            </TabsTrigger>
            <TabsTrigger
              value="invoices"
              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:text-teal-700 data-[state=active]:font-semibold data-[state=active]:bg-transparent px-4 text-xs sm:text-sm"
              data-testid="tab-invoices"
            >
              <Receipt className="h-4 w-4 mr-2" />
              Invoices & Payments ({invoices.length})
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ------------------------------------------------------------- */}
        {/* TAB 1: PATIENT OVERVIEW (Reference: media_1790016597403.png) */}
        {/* ------------------------------------------------------------- */}
        <TabsContent value="overview" className="mt-0 space-y-6">
          {/* Top Consultation / Appointment Launcher Banner */}
          <div className="bg-gradient-to-r from-teal-50/70 via-emerald-50/40 to-slate-50 border border-teal-200/80 rounded-xl p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-base sm:text-lg font-heading font-bold text-teal-950 flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-teal-600" />
                Start New Consultation or Schedule Appointment
              </h2>
              <p className="text-xs sm:text-sm text-slate-600">
                Initiate a live case sheet with prescription, or schedule a future follow-up visit.
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <AuthorizedButton
                permission="APPOINTMENT_WRITE"
                tooltipMessage="You don't have access to schedule appointments"
                variant="outline"
                size="sm"
                onClick={() => navigate(buildUrl(`appointments/new?patientId=${patient.id}`))}
                className="bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-xs text-xs font-semibold"
                data-testid="book-appointment-btn"
              >
                <Calendar className="mr-1.5 h-4 w-4 text-slate-600" />
                Schedule Appointment
              </AuthorizedButton>

              <AuthorizedButton
                permission="CONSULTATION_WRITE"
                tooltipMessage="You don't have access to start consultations"
                size="sm"
                onClick={() => navigate(buildUrl(`consultations/new?patientId=${patient.id}`))}
                className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm text-xs font-bold"
                data-testid="start-consultation-btn"
              >
                <Play className="mr-1.5 h-3.5 w-3.5 fill-current" />
                Start Consultation
              </AuthorizedButton>
            </div>
          </div>

          {/* Filter Bar (Year Chips + Search) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-xs">
            <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto">
              {['ALL', '2026', '2025', '2024'].map((yr) => (
                <button
                  key={yr}
                  type="button"
                  onClick={() => setYearFilter(yr)}
                  className={cn(
                    'px-3 py-1 text-xs font-semibold rounded-lg transition-colors',
                    yearFilter === yr
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                >
                  {yr === 'ALL' ? 'All Years' : yr}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search case sheets..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="h-8 pl-8 text-xs bg-slate-50 border-slate-200 rounded-lg"
              />
            </div>
          </div>

          {/* Case Sheets List */}
          <div className="space-y-4">
            {/* 1. ONGOING / DRAFT CASE SHEET (If exists) */}
            {ongoingConsultation && (
              <div className="bg-white rounded-xl border-2 border-amber-400/80 shadow-md p-5 transition-all">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-amber-100">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs uppercase tracking-wider flex items-center gap-1">
                      <Clock className="h-3 w-3 animate-spin" />
                      Ongoing Case Sheet
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {formatDate(ongoingConsultation.date, 'FULL')} • {ongoingConsultation.time || 'In Session'}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Financial Pending Badge */}
                    {(() => {
                      const inv = getInvoiceForConsultation(ongoingConsultation.id);
                      if (inv && inv.balanceAmount > 0) {
                        return (
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                            Pending • ₹{inv.balanceAmount} ↗
                          </span>
                        );
                      }
                      return (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                          Billing Pending
                        </span>
                      );
                    })()}

                    <Button
                      size="sm"
                      onClick={() => navigate(buildUrl(`consultations/${ongoingConsultation.id}`))}
                      className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-xs"
                    >
                      Resume Case Sheet
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="pt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-slate-400 font-medium">Attending Doctor:</span>
                    <p className="font-semibold text-slate-800 mt-0.5">
                      {formatDoctorName(ongoingConsultation.doctor)}
                    </p>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-slate-400 font-medium">Chief Complaints:</span>
                    <p className="text-slate-700 mt-0.5">
                      {ongoingConsultation.chiefComplaints || ongoingConsultation.symptoms || 'Draft in progress...'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 2. COMPLETED CASE SHEETS */}
            {completedConsultations.length > 0 ? (
              completedConsultations.map((cons) => {
                const isExpanded = !!expandedCaseSheets[cons.id];
                const inv = getInvoiceForConsultation(cons.id);
                const rxItems = cons.prescriptions || cons.prescription || [];

                return (
                  <div
                    key={cons.id}
                    className="bg-white rounded-xl border border-slate-200 shadow-xs hover:border-slate-300 transition-all overflow-hidden"
                  >
                    {/* Header Row */}
                    <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold font-heading text-slate-900">
                            Case Sheet — {cons.diagnosis || 'General Clinical Consultation'}
                          </span>
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-semibold">
                            Completed
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDate(cons.date, 'FULL')} • {formatDoctorName(cons.doctor)}
                        </p>
                      </div>

                      {/* Right Action Icons & Financial Status */}
                      <div className="flex items-center gap-2 self-end sm:self-center">
                        {/* Financial Pill */}
                        {inv ? (
                          inv.balanceAmount > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedInvoiceForModal(inv);
                                setInvoiceModalOpen(true);
                              }}
                              className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors"
                            >
                              Pending • ₹{inv.balanceAmount} ↗
                            </button>
                          ) : (
                            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Settled • ₹{inv.totalAmount} ✓
                            </span>
                          )
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedConsultationForPrint(cons);
                              setInvoiceModalOpen(true);
                            }}
                            className="px-2.5 py-1 rounded-full text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                          >
                            + Invoice
                          </button>
                        )}

                        {/* Print Prescription Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedConsultationForPrint(cons);
                          }}
                          className="h-8 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
                          title="Print Prescription"
                        >
                          <Printer className="h-3.5 w-3.5 sm:mr-1.5 text-teal-600" />
                          <span className="hidden sm:inline">Print Rx</span>
                        </Button>

                        {/* Expand / Collapse Details Chevron */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCaseSheetExpand(cons.id)}
                          className="h-8 px-2 text-slate-500 hover:text-slate-900"
                        >
                          {isExpanded ? (
                            <span className="flex items-center gap-1 text-xs">
                              Less <ChevronUp className="h-3.5 w-3.5" />
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs">
                              Details <ChevronDown className="h-3.5 w-3.5" />
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Summary Row */}
                    {!isExpanded && (
                      <div className="px-4 sm:px-5 pb-4 pt-1 flex flex-wrap items-center gap-4 text-xs text-slate-600 border-t border-slate-50 bg-slate-50/40">
                        <div>
                          <strong className="text-slate-800">Complaints:</strong>{' '}
                          {cons.chiefComplaints || cons.symptoms || 'Routine follow-up'}
                        </div>
                        {rxItems.length > 0 && (
                          <div>
                            <strong className="text-slate-800">Prescription:</strong>{' '}
                            {rxItems.length} medication{rxItems.length > 1 ? 's' : ''}
                          </div>
                        )}
                        {cons.vitals?.bloodPressure && (
                          <div>
                            <strong className="text-slate-800">BP:</strong>{' '}
                            {cons.vitals.bloodPressure.systolic}/{cons.vitals.bloodPressure.diastolic} mmHg
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expandable Case Sheet Content */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 bg-slate-50/50 p-4 sm:p-6 space-y-5 text-xs">
                        {/* 1. Complaints & Clinical Findings */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-3.5 bg-white rounded-lg border border-slate-200">
                            <span className="font-bold text-slate-900 block mb-1 text-xs uppercase tracking-wider">
                              Chief Complaints & Symptoms
                            </span>
                            <p className="text-slate-700">
                              {cons.chiefComplaints || cons.symptoms || 'No specific complaints noted'}
                            </p>
                          </div>

                          <div className="p-3.5 bg-white rounded-lg border border-slate-200">
                            <span className="font-bold text-slate-900 block mb-1 text-xs uppercase tracking-wider">
                              Clinical Examination Findings
                            </span>
                            <p className="text-slate-700">
                              {cons.examination || 'Systemic examination unremarkable.'}
                            </p>
                          </div>
                        </div>

                        {/* 2. Vitals Recorded */}
                        {cons.vitals && (
                          <div className="p-3.5 bg-white rounded-lg border border-slate-200">
                            <span className="font-bold text-slate-900 block mb-2 text-xs uppercase tracking-wider">
                              Vitals at Encounter
                            </span>
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                              <div className="p-2 rounded bg-slate-50">
                                <span className="text-[10px] text-slate-400">Blood Pressure</span>
                                <p className="font-bold text-slate-800">
                                  {cons.vitals.bloodPressure?.systolic || '--'}/
                                  {cons.vitals.bloodPressure?.diastolic || '--'} mmHg
                                </p>
                              </div>
                              <div className="p-2 rounded bg-slate-50">
                                <span className="text-[10px] text-slate-400">Heart Rate</span>
                                <p className="font-bold text-slate-800">
                                  {cons.vitals.heartRate ? `${cons.vitals.heartRate} bpm` : '--'}
                                </p>
                              </div>
                              <div className="p-2 rounded bg-slate-50">
                                <span className="text-[10px] text-slate-400">Temperature</span>
                                <p className="font-bold text-slate-800">
                                  {cons.vitals.temperature ? `${cons.vitals.temperature} °F` : '--'}
                                </p>
                              </div>
                              <div className="p-2 rounded bg-slate-50">
                                <span className="text-[10px] text-slate-400">Oxygen (SpO2)</span>
                                <p className="font-bold text-slate-800">
                                  {cons.vitals.oxygenSaturation ? `${cons.vitals.oxygenSaturation}%` : '--'}
                                </p>
                              </div>
                              <div className="p-2 rounded bg-slate-50">
                                <span className="text-[10px] text-slate-400">Weight</span>
                                <p className="font-bold text-slate-800">
                                  {cons.vitals.weight ? `${cons.vitals.weight} kg` : '--'}
                                </p>
                              </div>
                              <div className="p-2 rounded bg-slate-50">
                                <span className="text-[10px] text-slate-400">Height</span>
                                <p className="font-bold text-slate-800">
                                  {cons.vitals.height ? `${cons.vitals.height} cm` : '--'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 3. Prescribed Medicines Table */}
                        {rxItems.length > 0 && (
                          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                            <div className="px-4 py-2.5 bg-slate-100/70 border-b border-slate-200 flex items-center justify-between">
                              <span className="font-bold text-slate-900 text-xs uppercase tracking-wider">
                                Prescribed Medications ({rxItems.length})
                              </span>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-100">
                                  <tr>
                                    <th className="p-3">Medicine</th>
                                    <th className="p-3">Dosage / Amt</th>
                                    <th className="p-3">Frequency</th>
                                    <th className="p-3">Duration</th>
                                    <th className="p-3">Instructions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {rxItems.map((rx, rIdx) => (
                                    <tr key={rIdx} className="hover:bg-slate-50/50">
                                      <td className="p-3 font-semibold text-slate-900">
                                        {rx.medicineName || rx.medication}
                                      </td>
                                      <td className="p-3 text-slate-600">
                                        {rx.dosage || rx.amount || '1 tablet'}
                                      </td>
                                      <td className="p-3 text-slate-700 font-medium">
                                        {rx.frequency}
                                      </td>
                                      <td className="p-3 text-slate-600">{rx.duration}</td>
                                      <td className="p-3 text-slate-500 italic">
                                        {rx.instructions || rx.notes || '--'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Doctor Notes & Follow up */}
                        {(cons.doctorNotes || cons.notes || cons.followUpDate) && (
                          <div className="p-3.5 bg-teal-50/50 rounded-lg border border-teal-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <span className="font-bold text-teal-950 text-xs uppercase tracking-wider block">
                                Doctor Notes & Patient Advice
                              </span>
                              <p className="text-slate-700 mt-0.5">
                                {cons.doctorNotes || cons.notes || 'Maintain hydration and follow-up as advised.'}
                              </p>
                            </div>
                            {cons.followUpDate && (
                              <div className="text-left sm:text-right shrink-0">
                                <span className="text-[10px] text-slate-400 uppercase font-semibold">
                                  Next Follow-up
                                </span>
                                <p className="font-bold text-teal-900">
                                  {formatDate(cons.followUpDate, 'FULL')}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Stethoscope className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <h3 className="font-heading font-semibold text-slate-800 text-base">
                  No Case Sheets Found
                </h3>
                <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                  {searchFilter || yearFilter !== 'ALL'
                    ? 'No consultation records match your active search or year filter.'
                    : 'Start the first clinical consultation case sheet for this patient.'}
                </p>
                <Button
                  onClick={() => navigate(buildUrl(`consultations/new?patientId=${patient.id}`))}
                  className="mt-4 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Start First Case Sheet
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ------------------------------------------------------------- */}
        {/* TAB 2: VISUAL EXAMINATION & VITALS TREND                      */}
        {/* ------------------------------------------------------------- */}
        <TabsContent value="examination" className="mt-0 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Activity className="h-4 w-4 text-teal-600" />
                Clinical Findings & Vitals Trajectory
              </CardTitle>
              <CardDescription className="text-xs">
                Historical physiological parameters and physical examination records across visits.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {consultations.some((c) => c.vitals) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {consultations.map((c) => (
                    <div
                      key={c.id}
                      className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3"
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                        <span className="font-bold text-xs text-slate-900">
                          {formatDate(c.date, 'FULL')}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {formatDoctorName(c.doctor)}
                        </span>
                      </div>

                      {c.vitals ? (
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-slate-400">BP:</span>{' '}
                            <strong className="text-slate-800">
                              {c.vitals.bloodPressure?.systolic || '--'}/{c.vitals.bloodPressure?.diastolic || '--'}
                            </strong>
                          </div>
                          <div>
                            <span className="text-slate-400">HR:</span>{' '}
                            <strong className="text-slate-800">
                              {c.vitals.heartRate ? `${c.vitals.heartRate} bpm` : '--'}
                            </strong>
                          </div>
                          <div>
                            <span className="text-slate-400">Temp:</span>{' '}
                            <strong className="text-slate-800">
                              {c.vitals.temperature ? `${c.vitals.temperature}°F` : '--'}
                            </strong>
                          </div>
                          <div>
                            <span className="text-slate-400">SpO2:</span>{' '}
                            <strong className="text-slate-800">
                              {c.vitals.oxygenSaturation ? `${c.vitals.oxygenSaturation}%` : '--'}
                            </strong>
                          </div>
                          <div>
                            <span className="text-slate-400">Weight:</span>{' '}
                            <strong className="text-slate-800">
                              {c.vitals.weight ? `${c.vitals.weight} kg` : '--'}
                            </strong>
                          </div>
                          <div>
                            <span className="text-slate-400">Height:</span>{' '}
                            <strong className="text-slate-800">
                              {c.vitals.height ? `${c.vitals.height} cm` : '--'}
                            </strong>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 italic">No vitals captured</p>
                      )}

                      {c.examination && (
                        <div className="pt-2 border-t border-slate-200 text-xs">
                          <span className="text-slate-500 font-medium">Exam Notes:</span>
                          <p className="text-slate-700 mt-0.5 line-clamp-3">{c.examination}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500 text-xs">
                  <Activity className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  No examination or vitals data recorded yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------------------------------------------------------- */}
        {/* TAB 3: MEDICATIONS (MASTER LEDGER)                            */}
        {/* ------------------------------------------------------------- */}
        <TabsContent value="medications" className="mt-0 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Pill className="h-4 w-4 text-teal-600" />
                Comprehensive Medication History
              </CardTitle>
              <CardDescription className="text-xs">
                Master record of all pharmaceutical drugs prescribed across encounters.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {allMedications.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="p-3">Medication Name</th>
                        <th className="p-3">Dosage</th>
                        <th className="p-3">Frequency</th>
                        <th className="p-3">Duration</th>
                        <th className="p-3">Instructions</th>
                        <th className="p-3">Prescribed By</th>
                        <th className="p-3">Encounter Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {allMedications.map((rx, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/70">
                          <td className="p-3 font-semibold text-slate-900">
                            {rx.medicineName || rx.medication}
                          </td>
                          <td className="p-3 text-slate-700">{rx.dosage || rx.amount || '1 tablet'}</td>
                          <td className="p-3 text-slate-800 font-medium">{rx.frequency}</td>
                          <td className="p-3 text-slate-600">{rx.duration}</td>
                          <td className="p-3 text-slate-500 italic">{rx.instructions || '--'}</td>
                          <td className="p-3 text-slate-700">{formatDoctorName(rx.doctor)}</td>
                          <td className="p-3 text-slate-500">{formatDate(rx.visitDate, 'DEFAULT')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500 text-xs">
                  <Pill className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  No medications prescribed yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------------------------------------------------------- */}
        {/* TAB 4: MEDICAL BACKGROUND                                      */}
        {/* ------------------------------------------------------------- */}
        <TabsContent value="background" className="mt-0 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Allergies & Adverse Reactions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {patient.allergies?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {patient.allergies.map((allergy, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="bg-red-50 text-red-700 border-red-200 text-xs px-2.5 py-1"
                      >
                        ⚠️ {allergy}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">No known pharmaceutical or food allergies.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Heart className="h-4 w-4 text-amber-500" />
                  Chronic Conditions & Past Illnesses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {patient.chronicConditions?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {patient.chronicConditions.map((cond, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-200 text-xs px-2.5 py-1"
                      >
                        {cond}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">No chronic medical conditions recorded.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ------------------------------------------------------------- */}
        {/* TAB 5: PAPERWORK & DOCUMENTS                                  */}
        {/* ------------------------------------------------------------- */}
        <TabsContent value="paperwork" className="mt-0 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <FileCheck className="h-4 w-4 text-teal-600" />
                  Clinical Paperwork & Lab Reports
                </CardTitle>
                <CardDescription className="text-xs">
                  Scanned reports, diagnostic files, and patient documents.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" className="text-xs">
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Upload Document
              </Button>
            </CardHeader>
            <CardContent>
              {files.length > 0 ? (
                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {files.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3.5 hover:bg-slate-50 text-xs"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-teal-50 text-teal-700">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{file.name}</p>
                          <p className="text-[11px] text-slate-400">
                            {(file.size / 1024).toFixed(1)} KB • Uploaded {formatDate(file.uploadedAt, 'DEFAULT')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500">
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500 text-xs">
                  <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  No paperwork or documents uploaded yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------------------------------------------------------- */}
        {/* TAB 6: INVOICES & PAYMENTS LEDGER                             */}
        {/* ------------------------------------------------------------- */}
        <TabsContent value="invoices" className="mt-0 space-y-6">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-3">
              <div>
                <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-teal-600" />
                  Patient Invoices & Payment Ledger
                </CardTitle>
                <CardDescription className="text-xs">
                  Complete billing transactions, receipts, and payment settlements.
                </CardDescription>
              </div>

              {totalOutstanding > 0 && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Total Pending</span>
                    <p className="text-sm font-bold text-amber-700">₹{totalOutstanding.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {invoices.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="p-3">Invoice #</th>
                        <th className="p-3">Issued Date</th>
                        <th className="p-3">Doctor</th>
                        <th className="p-3">Total (₹)</th>
                        <th className="p-3">Paid (₹)</th>
                        <th className="p-3">Balance (₹)</th>
                        <th className="p-3">Status</th>
                        <th className="p-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-slate-50/70">
                          <td className="p-3 font-mono font-bold text-slate-900">
                            {inv.invoiceNumber}
                          </td>
                          <td className="p-3 text-slate-500">
                            {formatDate(inv.issuedAt || inv.createdAt, 'DEFAULT')}
                          </td>
                          <td className="p-3 text-slate-700">{inv.doctorName || '--'}</td>
                          <td className="p-3 font-semibold text-slate-900">
                            ₹{Number(inv.totalAmount).toLocaleString('en-IN')}
                          </td>
                          <td className="p-3 font-semibold text-emerald-700">
                            ₹{Number(inv.paidAmount).toLocaleString('en-IN')}
                          </td>
                          <td className="p-3 font-bold text-slate-900">
                            {inv.balanceAmount > 0 ? (
                              <span className="text-amber-700">₹{Number(inv.balanceAmount).toLocaleString('en-IN')}</span>
                            ) : (
                              <span className="text-slate-400">₹0</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge
                              className={cn(
                                'text-[10px] font-bold uppercase',
                                inv.status === 'PAID'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : inv.status === 'PARTIALLY_PAID'
                                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                                  : 'bg-red-50 text-red-700 border-red-200'
                              )}
                            >
                              {inv.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {inv.balanceAmount > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setActiveInvoiceForPayment(inv);
                                    setPaymentRecordModalOpen(true);
                                  }}
                                  className="h-7 px-2.5 text-[11px] bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                                >
                                  Collect
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedInvoiceForModal(inv);
                                  setInvoiceModalOpen(true);
                                }}
                                className="h-7 px-2 text-[11px] text-slate-600 border-slate-200"
                              >
                                View
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setActiveInvoiceForPrint(inv);
                                  setInvoicePrintModalOpen(true);
                                }}
                                className="h-7 w-7 text-slate-500 hover:text-slate-900"
                                title="Print Invoice"
                              >
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500 text-xs">
                  <Receipt className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  No invoices generated for this patient yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MODALS */}
      {/* 1. Prescription Print Modal */}
      {selectedConsultationForPrint && (
        <PrescriptionPrintModal
          open={!!selectedConsultationForPrint}
          onOpenChange={(isOpen) => {
            if (!isOpen) setSelectedConsultationForPrint(null);
          }}
          consultation={selectedConsultationForPrint}
          patient={patient}
          doctor={selectedConsultationForPrint?.doctor}
          clinicName={activeClinic?.name}
          hospitalName={activeClinic?.hospitalName || 'PulseFlow Health'}
        />
      )}

      {/* 2. Invoice View / Edit Modal */}
      {invoiceModalOpen && (
        <InvoiceModal
          open={invoiceModalOpen}
          onOpenChange={(isOpen) => {
            setInvoiceModalOpen(isOpen);
            if (!isOpen) setSelectedInvoiceForModal(null);
          }}
          consultation={selectedConsultationForPrint || null}
          patient={patient}
          doctor={selectedConsultationForPrint?.doctor || null}
          clinicId={activeClinicId}
          hospitalId={hospitalId}
          onInvoiceCreated={() => {
            loadPatientData();
          }}
        />
      )}

      {/* 3. Record Payment Modal */}
      {paymentRecordModalOpen && activeInvoiceForPayment && (
        <PaymentRecordModal
          open={paymentRecordModalOpen}
          onOpenChange={(isOpen) => {
            setPaymentRecordModalOpen(isOpen);
            if (!isOpen) setActiveInvoiceForPayment(null);
          }}
          invoice={activeInvoiceForPayment}
          clinicId={activeClinicId}
          hospitalId={hospitalId}
          onPaymentSuccess={() => {
            loadPatientData();
          }}
        />
      )}

      {/* 4. Invoice Print Modal */}
      {invoicePrintModalOpen && activeInvoiceForPrint && (
        <InvoicePrintModal
          open={invoicePrintModalOpen}
          onOpenChange={(isOpen) => {
            setInvoicePrintModalOpen(isOpen);
            if (!isOpen) setActiveInvoiceForPrint(null);
          }}
          invoice={activeInvoiceForPrint}
          clinicName={activeClinic?.name}
          hospitalName={activeClinic?.hospitalName || 'PulseFlow Health'}
        />
      )}
    </div>
  );
};

export default PatientDetailPage;
